import {
  CreateHealthRecordBodyHealthRecordGenderEnum,
  CreateVaccinationActBodyVaccinationAct as ProCreateVaccinationAct,
} from "@syadem/kairos-pro-js";
import { RefObject, useCallback, useState } from "react";
import { DomainHealthRecord } from "../../../../domain/healthRecord";
import { formatDateForApi } from "../../../../utils/date";
import { dayjs } from "../../../../utils/dayjs";
import { useDaphne } from "../../../hooks/useDaphne";
import { useI18n } from "../../../hooks/useI18n";
import { useServiceBus } from "../../../hooks/useServiceBus";
import { buildHealthRecordPath, ImportDestinationOption } from "../evcImportDestinations";
import { ImportProgress, NewHealthRecordDraft, VaccinationToImport } from "./types";

type EvcImportSubmitStatus = "idle" | "importing" | "done" | "error";

export function useEvcImportSubmit({
  targetMode,
  selectedDestination,
  importDestinations,
  selectedExistingHealthRecord,
  selectedVaccinationIds,
  vaccinations,
  draft,
  needsZipCode,
  validateZipCode,
  genderRef,
  zipCodeRef,
  setInvalidGender,
  setInvalidZipCode,
  markSelectedHealthRecordRequired,
}: {
  targetMode: "new" | "existing";
  selectedDestination: ImportDestinationOption | null;
  importDestinations: ImportDestinationOption[];
  selectedExistingHealthRecord?: DomainHealthRecord;
  selectedVaccinationIds: Set<number>;
  vaccinations: VaccinationToImport[];
  draft: NewHealthRecordDraft;
  needsZipCode: boolean;
  validateZipCode: (value: string) => boolean;
  genderRef: RefObject<HTMLDivElement | null>;
  zipCodeRef: RefObject<HTMLDivElement | null>;
  setInvalidGender: (invalid: boolean) => void;
  setInvalidZipCode: (invalid: boolean) => void;
  markSelectedHealthRecordRequired: () => void;
}) {
  const { t } = useI18n();
  const daphne = useDaphne();
  const serviceBus = useServiceBus();
  const [status, setStatus] = useState<EvcImportSubmitStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<ImportProgress>({ done: 0, total: 0, errors: 0 });
  const [healthRecordId, setHealthRecordId] = useState<string | null>(null);
  const [submittedDestinationId, setSubmittedDestinationId] = useState<string | null>(null);

  const submit = useCallback(async () => {
    if (!selectedDestination) {
      return;
    }

    if (targetMode === "existing" && !selectedExistingHealthRecord) {
      markSelectedHealthRecordRequired();
      return;
    }

    if (targetMode === "new") {
      if (!draft.gender) {
        setInvalidGender(true);
        genderRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      if (needsZipCode && !validateZipCode(draft.zipCode)) {
        setInvalidZipCode(true);
        zipCodeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }

    setStatus("importing");
    setErrorMessage(null);
    setSubmittedDestinationId(selectedDestination.id);
    const selectedVaccinations = vaccinations.filter((vaccination) => selectedVaccinationIds.has(vaccination.id));
    setImportProgress({ done: 0, total: selectedVaccinations.length, errors: 0 });

    try {
      let targetHealthRecordId = selectedExistingHealthRecord?.id ?? null;

      if (targetMode === "new") {
        const result = await serviceBus.dispatch({
          type: "createHealthRecord",
          organizationId: selectedDestination.organizationId,
          teamId: selectedDestination.teamId,
          healthRecord: {
            firstNames: draft.firstName.trim(),
            lastName: draft.lastName.trim(),
            customaryName: null,
            birthDate: formatDateForApi(draft.birthDate!.toDate()),
            gender: draft.gender as CreateHealthRecordBodyHealthRecordGenderEnum,
            externalId: null,
            zipCode: draft.zipCode.trim() || null,
            birthCountryCode: null,
            birthCityCode: null,
          },
        });

        if (result.type !== "success" || !result.value?.id) {
          setErrorMessage(t("evc_onboarding.create_hr_error"));
          setStatus("error");
          return;
        }

        targetHealthRecordId = result.value.id;
      }

      if (!targetHealthRecordId) {
        setErrorMessage(t("evc_onboarding.existing_record_required"));
        setStatus("error");
        return;
      }

      setHealthRecordId(targetHealthRecordId);

      let done = 0;
      let errors = 0;

      for (const vaccination of selectedVaccinations) {
        const vaccine = daphne.repositories.vaccines.all().find((candidate) => candidate.code === vaccination.nuvaCode);
        if (!vaccine) {
          errors++;
          done++;
          setImportProgress({ done, total: selectedVaccinations.length, errors });
          continue;
        }

        try {
          await serviceBus.dispatch({
            type: "createVaccinationAct",
            organizationId: selectedDestination.organizationId,
            teamId: selectedDestination.teamId,
            healthRecordId: targetHealthRecordId,
            vaccinationAct: {
              performedOn: dayjs.utc(vaccination.performedOn).toDate(),
              vaccineId: vaccine.id,
              certificationMethod: "proof",
              countryCode: vaccination.countryCode || undefined,
              booster: vaccination.booster || undefined,
            } as ProCreateVaccinationAct,
          });
        } catch {
          errors++;
        }

        done++;
        setImportProgress({ done, total: selectedVaccinations.length, errors });
      }

      setStatus("done");
    } catch {
      setErrorMessage(targetMode === "new" ? t("evc_onboarding.create_hr_error") : t("evc_onboarding.import_error"));
      setStatus("error");
    }
  }, [
    daphne,
    draft.birthDate,
    draft.firstName,
    draft.gender,
    draft.lastName,
    draft.zipCode,
    genderRef,
    markSelectedHealthRecordRequired,
    needsZipCode,
    selectedDestination,
    selectedExistingHealthRecord,
    selectedVaccinationIds,
    serviceBus,
    setInvalidGender,
    setInvalidZipCode,
    t,
    targetMode,
    vaccinations,
    validateZipCode,
    zipCodeRef,
  ]);

  const buildViewRecordPath = useCallback(() => {
    const destination =
      importDestinations.find((candidate) => candidate.id === submittedDestinationId) ?? selectedDestination;

    if (!healthRecordId || !destination) {
      return null;
    }

    return buildHealthRecordPath(destination, healthRecordId);
  }, [healthRecordId, importDestinations, selectedDestination, submittedDestinationId]);

  return {
    status,
    errorMessage,
    importProgress,
    submit,
    buildViewRecordPath,
  };
}
